import { AdvertPayload } from '../@types/ad-payload';
import { logError } from '../logger';
import { buster, safeWhile } from './util';

/**
 * These are element attributes which we may modify, so we store a copy of the values against the same
 * element prefixed with the below. On unload we can then restore the original values.
 * All attributes that we adjust should be stored here so that we can clean up properly should
 * we modify attribute names granularly
 */
const ATTRIBUTE_NAMES = ['min-height', 'min-width'];
const ATTRIBUTE_PREFIX = 'mobkoi-orig-';

export const isAmpPage = (globalScope: Window = window) => {
  const html = globalScope.document.getElementsByTagName('html')[0];

  return Boolean(
    html.hasAttribute('amp') ||
      html.hasAttribute('⚡') ||
      globalScope.AMP_CONTEXT_DATA ||
      globalScope.sf_?.cfg?._context,
  );
};

export const getLastFriendlyFrameElement = (globalScope: Window = window) => {
  let frameElement: HTMLElement | null = null;
  let currentWindow: Window | null = globalScope;

  try {
    safeWhile(
      () => !!currentWindow?.frameElement,
      () => {
        // If unfriendly iframe, the if will throw an error
        if (currentWindow?.frameElement?.ownerDocument.location.href) {
          frameElement = currentWindow.frameElement as HTMLElement;
          currentWindow = frameElement?.ownerDocument.defaultView || null;
        }
      },
      (elapsed) => {
        logError({
          error: `getLastFriendlyFrameElement timed out, elapsed time: ${elapsed}`,
          impressionId: '',
          tagId: '0',
          reason: 'lib-err-duration',
        });
      },
    );
  } catch (e) {
    // not a friendly iframe
  }

  return frameElement as HTMLElement | null;
};

export const getActiveWindow = (globalScope: Window = window) => {
  const frameElement = getLastFriendlyFrameElement(globalScope);

  return frameElement ? frameElement.ownerDocument.defaultView : globalScope;
};

/**
 * If we can't access window.top.addEventListener we're in unfriendly
 * territory, also, safari doesn't error when blocking a cross origin iframe
 * accessing window.top.addEventListener because reasons
 * So we need to wrap this functionality
 */
export const hasWindowTopEventListener = (globalScope: Window = window) => {
  let hasEventListener = false;
  try {
    hasEventListener = !!globalScope.top?.addEventListener;
  } catch (e) {
    hasEventListener = false;
  }

  return hasEventListener;
};

/**
 * Its possible to have a frame element and still be 'unfriendly'
 */
export const getTopWindow = (
  globalScope: Window = window,
  frameElement: ReturnType<typeof getLastFriendlyFrameElement>,
) => {
  let hasEventListener = hasWindowTopEventListener(globalScope);

  // globalScope.top is possibly null, so just protect ourselves
  return frameElement && hasEventListener
    ? globalScope.top || globalScope
    : globalScope;
};

export const hasCreateElementOverride = (globalScope: Window = window) => {
  try {
    return (
      globalScope.document.createElement.toString().indexOf('[native code]') < 0
    );
  } catch (x) {
    return false;
  }
};

export const isDOMNode = (node?: any): node is Element =>
  node && typeof node === 'object' && node.nodeType === 1;

export const isIframe = (node?: any): node is HTMLIFrameElement =>
  isDOMNode(node) && node.tagName === 'IFRAME';

/**
 * Given a starting DOM node, walk up the ancestor hierarchy passing each node to the specified function, until
 * it returns true.
 *
 * @param {DOMElement} node
 * @param {Function} fn
 */
export const walkDomTree = (
  node: HTMLElement | null,
  fn: (node: HTMLElement) => boolean,
) => {
  safeWhile(
    () => !!node,
    (breakFn) => {
      if (fn(node!)) {
        breakFn();
        return;
      }
      node = node!.parentElement;
    },
    (elapsed) => {
      logError({
        error: `walkDomTree timed out, elapsed time: ${elapsed}`,
        detail: elapsed,
        impressionId: '',
        tagId: '0',
        reason: 'lib-err-duration',
      });
    },
  );
  return node;
};

/**
 * Given a simplified css selector string, return a function which when given a node will return true/false
 * if the node matches the selector. Only simple selectors "#someid", ".someclass" or "sometag" are supported.
 */
export const getSelectorFunction = (selector: string = '') => {
  if (!selector) {
    return function () {
      return false;
    };
  }

  selector = selector.toLowerCase();

  const selectorSymbol = selector[0];

  if (selectorSymbol === '#') {
    return (node: HTMLElement) => node.id === selector.substring(1);
  }
  if (selectorSymbol === '.') {
    return (node: HTMLElement) =>
      node.classList.contains(selector.substring(1));
  }

  return (node: HTMLElement) => node.tagName.toLowerCase() === selector;
};

export const getLastAncestor = (node: HTMLElement, selector?: string) =>
  walkDomTree(node, getSelectorFunction(selector));

export const createMobkoiContainer = (
  html: string,
  doc: Document,
  padding: number,
  styles: Partial<CSSStyleDeclaration> = {},
) => {
  const container = (doc || document).createElement('div');

  container.id = `mobkoi-${buster()}`;
  container.innerHTML = html;
  Object.assign(container.style, {
    margin: 0,
    padding: `${padding} 0 ${padding} 0`,
    border: 0,
    position: 'relative',
    background: '#FFF',
    ...styles,
  });

  return container;
};

/**
 * Load a script url, optionally call the onload or onerror handlers and also
 * remove the resultant SCRIPT element from the DOM.
 */
export const loadScript = (
  url: string,
  onload: (duration: number) => void = () => {},
  onerror: (duration: number) => void = () => {},
) => {
  const scriptTag = document.createElement('script');
  const startTime = Date.now();

  const removeScriptTag = () => scriptTag.parentNode?.removeChild(scriptTag);

  Object.assign(scriptTag, {
    async: 1,
    type: 'application/javascript',
    src: url,
    onload: () => {
      const duration = Date.now() - startTime;
      onload(duration);
      removeScriptTag();
    },
    onerror: () => {
      const duration = Date.now() - startTime;
      onerror(duration);
      removeScriptTag();
    },
  });

  const appendScript = () => {
    const headElement = document.querySelector('head');
    if (headElement) {
      headElement.appendChild(scriptTag);
    } else {
      throw new Error('HEAD element not found in the document.');
    }
  };

  // Only append the script tag if the document is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', appendScript);
  } else {
    appendScript();
  }

  return scriptTag;
};

export const createIframeSrcdoc = ({
  clazz,
  width,
  height,
  markup,
  hide,
}: {
  clazz: string;
  width: string;
  height: string;
  markup: string;
  hide: boolean;
}) => {
  let i = document.createElement('iframe');
  i.id = clazz;
  // @ts-ignore
  i.style = `width:${width}; height:${height}; margin:0; padding:0; position:absolute; left:0; top:0; border:0`;
  if (hide) {
    i.style.overflow = 'hidden';
    i.style.zIndex = '-1';
  }
  i.srcdoc = `<html><head><script>var inDapIF=true;</script></head><body style="margin:0; padding:0;">${markup}</body></html>`;

  return i;
};

export const getMarkupTemplates = (): {
  html: string[];
  children: Element[];
  clickTrackers: {
    url: string;
    type: 'script' | 'pixel' | 'url' | 'redirect';
  }[];
  trackers: AdvertPayload['trackers'];
  triggers: (() => void)[];
} => ({
  html: [],
  children: [],
  clickTrackers: [],
  trackers: [],
  triggers: [],
});

export const getNodeDimension = (
  node: HTMLElement,
  dimension: 'width' | 'height',
) => {
  const rect = node.getBoundingClientRect();
  const firstFoundDimension = [
    node.style[dimension],
    // @ts-ignore
    node[dimension],
    // @ts-ignore
    node.style['min' + dimension.charAt(0).toUpperCase() + dimension.slice(1)],
    rect[dimension],
  ].find(Boolean);
  return parseInt(firstFoundDimension, 10) || 0;
};

export const getNodeHeight = (node: HTMLElement) =>
  getNodeDimension(node, 'height');

export const getNodeWidth = (node: HTMLElement) =>
  getNodeDimension(node, 'width');

const touchedElements = new Set<HTMLElement>();

/**
 * For the specified element, Copy values from specified attribute names into another attribute
 * with the name prefix + name.
 * @param {DOMElement*} elem
 * @param {Array{String}} names
 * @param {String} prefix
 */
export const saveStyles = (
  elem: HTMLElement,
  names = ATTRIBUTE_NAMES,
  prefix = ATTRIBUTE_PREFIX,
) => {
  const styles =
    elem.ownerDocument.defaultView?.getComputedStyle(elem) ||
    ({} as CSSStyleDeclaration);

  names.forEach((n) => {
    if (ATTRIBUTE_NAMES.indexOf(n) === -1) {
      console.error(
        'Attribute name ' +
          n +
          ' must be added to ATTRIBUTE_NAMES to ensure correct clean up',
      );
    }

    let v = styles[n as keyof CSSStyleDeclaration];
    //  the 0 is so we ignore "0", "0px", "0em", "0%" etc.
    // @ts-ignore
    if (v && v[0] !== '0') {
      elem.setAttribute(prefix + n, String(v));
    }
  });

  touchedElements.add(elem);
};

/**
 * For the specified element, restore attribute values from prefix + names.
 * Returns true if any attributes were restored.
 * @param {DOMElement*} elem
 * @param {Array{String}} names
 * @param {String} prefix
 * @returns {Boolean}
 */
export const restoreStyles = (
  elem: HTMLElement,
  names = ATTRIBUTE_NAMES,
  prefix = ATTRIBUTE_PREFIX,
) => {
  let restored = false;
  names.forEach((n) => {
    if (ATTRIBUTE_NAMES.indexOf(n) === -1) {
      console.error(
        'Attribute name ' +
          n +
          ' must be added to ATTRIBUTE_NAMES to ensure correct clean up',
      );
    }

    let v = elem.getAttribute(prefix + n);
    if (v) {
      // @ts-ignore
      elem.style[n] = v;
      restored = true;
    }
    elem.removeAttribute(prefix + n);
  });

  touchedElements.delete(elem);

  return restored;
};

export const restoreTouchedElementsToOriginalState = () => {
  touchedElements.forEach((node) => {
    if (document.body.contains(node)) {
      restoreStyles(node);
    }
  });
};
